import { Navigate, Outlet, useLocation } from 'react-router-dom'

import { useAuth } from './AuthProvider'
import { FxSpinner } from '@/components/fx'
import { authScreenFor } from '@/pages/auth/afterAuth'

/**
 * Gates a route on a session. Access control itself is enforced by the
 * gateway on every call — this only decides which screen to show, and must
 * never be the only thing standing between a person and someone else's data.
 */
export function ProtectedRoute() {
  const { signedIn, loading, impersonationEnded } = useAuth()
  const location = useLocation()

  if (loading) {
    return (
      <div className="grid h-full place-items-center">
        <FxSpinner label="Sitzung wird geprüft …" />
      </div>
    )
  }

  if (!signedIn) {
    // An impersonation session that expired or was refused is not an
    // ordinary sign-out: this browser tab was never signed in on its own
    // account, so bouncing it to /login would read as if it had been. Stop
    // here instead, in place, with the word for what actually happened.
    if (impersonationEnded) {
      return (
        <div className="grid h-full place-items-center p-6 text-center">
          <div className="max-w-[420px]">
            <h1 className="text-panel-title text-text">Ansicht beendet</h1>
            <p className="mt-2 text-body text-text-muted">
              Diese Nutzeransicht ist abgelaufen oder wurde beendet. Sie können diesen Tab schließen.
            </p>
          </div>
        </div>
      )
    }
    // Registration rather than sign-in for someone mid-funnel — they have no
    // account yet. `state.from` is where they were going, so whichever screen
    // they land on can put them back there afterwards.
    return (
      <Navigate
        to={authScreenFor(location.pathname)}
        replace
        state={{ from: location.pathname + location.search }}
      />
    )
  }

  return <Outlet />
}
